<script setup>
import { computed, onActivated, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import RowMenu from 'dashboard/components-next/polaris/cards/RowMenu.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'dashboard/components-next/polaris/ui/dropdown-menu';
import { Settings16, TrashCan16 } from '@carbon/icons-vue';
import {
  channelTypeByKey,
  channelTypeOfInbox,
  isCreationAvailable,
  isCreationListed,
} from './channelTypes';

// One page per channel type (phase 9.6, D-S3 / R2-9-3 = A): the inboxes of that type in the kit's table (brand,
// name, the channel's own name, ⋮ with Settings and Delete), "New inbox" (the type's form; a menu when the type
// has several, as social channels and voice do) and the kit's empty state. The same getter, search, delete flow
// and admin rule as the single inbox list had.
const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const { currentAccount } = useAccount();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const searchQuery = ref('');

const inboxes = useMapGetter('inboxes/getInboxes');
const uiFlags = useMapGetter('inboxes/getUIFlags');

onActivated(() => {
  store.dispatch('inboxes/get');
});

const channelType = computed(
  () => channelTypeByKey(route.params.channelType) || channelTypeByKey('api')
);
const typeName = computed(() =>
  t(`SETTINGS_CHANNELS.TYPES.${channelType.value.key.toUpperCase()}.NAME`)
);
const typeSentence = computed(() =>
  t(`SETTINGS_CHANNELS.TYPES.${channelType.value.key.toUpperCase()}.SENTENCE`)
);

const inboxesList = computed(() =>
  inboxes.value
    ?.filter(inbox => channelTypeOfInbox(inbox) === channelType.value.key)
    .sort((a, b) => a.name.localeCompare(b.name))
);

const filteredInboxesList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return inboxesList.value;
  return picoSearch(inboxesList.value, query, ['name', 'channel_type']);
});

// The new-inbox forms of this type the account can use (their names are the woot's channel titles).
const creationForms = computed(() =>
  channelType.value.create
    .filter(isCreationListed)
    .filter(key => isCreationAvailable(key, currentAccount.value?.features))
    .map(key => ({
      key,
      label: t(`INBOX_MGMT.ADD.AUTH.CHANNEL.${key.toUpperCase()}.TITLE`),
    }))
);
const createInbox = key =>
  router.push({
    name: 'settings_inboxes_page_channel',
    params: { sub_page: key },
  });

const tableHeaders = computed(() => [
  t('SETTINGS_CHANNELS.INBOX'),
  t('SETTINGS_CHANNELS.CHANNEL'),
  t('SETTINGS_TABLE.ACTIONS'),
]);

const rowMenuItems = computed(() => [
  {
    label: t('SETTINGS_CHANNELS.SETTINGS'),
    value: 'settings',
    icon: Settings16,
  },
  {
    label: t('SETTINGS_TABLE.DELETE'),
    value: 'delete',
    icon: TrashCan16,
    destructive: true,
  },
]);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};
const onRowAction = (action, inbox) => {
  if (action === 'delete') {
    openDelete(inbox);
    return;
  }
  router.push({ name: 'settings_inbox_show', params: { inboxId: inbox.id } });
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="t('SETTINGS_CHANNELS.EMPTY_TITLE')"
    :is-loading="uiFlags.isFetching && !inboxes.length"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="typeName"
        :description="typeSentence"
        :search-placeholder="
          inboxesList.length ? t('INBOX_MGMT.SEARCH_PLACEHOLDER') : ''
        "
      >
        <template v-if="inboxesList.length" #count>
          {{ t('INBOX_MGMT.COUNT', { n: inboxesList.length }) }}
        </template>
        <template v-if="isAdmin && creationForms.length" #actions>
          <Button
            v-if="creationForms.length === 1"
            :label="t('SETTINGS_CHANNELS.NEW_INBOX')"
            size="sm"
            @click="createInbox(creationForms[0].key)"
          />
          <DropdownMenu v-else>
            <DropdownMenuTrigger as-child>
              <Button
                :label="t('SETTINGS_CHANNELS.NEW_INBOX')"
                size="sm"
                trailing-icon
                icon="i-lucide-chevron-down"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                v-for="form in creationForms"
                :key="form.key"
                @select="createInbox(form.key)"
              >
                {{ form.label }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </template>
      </BaseSettingsHeader>
    </template>
    <template v-if="isAdmin && creationForms.length" #emptyActions>
      <p class="type-polaris-body text-polaris-muted-foreground">
        {{ t('SETTINGS_CHANNELS.EMPTY_TEXT') }}
      </p>
      <div class="flex flex-wrap justify-center gap-2">
        <Button
          v-for="form in creationForms"
          :key="form.key"
          :label="
            creationForms.length === 1
              ? t('SETTINGS_CHANNELS.NEW_INBOX')
              : form.label
          "
          size="sm"
          outline
          @click="createInbox(form.key)"
        />
      </div>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredInboxesList"
        :no-data-message="t('INBOX_MGMT.NO_RESULTS')"
        pin-last-column
        :clearable="!!searchQuery"
        @clear="searchQuery = ''"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="inbox in items" :key="inbox.id" :item="inbox">
            <BaseTableCell wrap>
              <span class="flex min-w-0 items-center gap-3">
                <span
                  class="flex size-8 shrink-0 items-center justify-center rounded-polaris-sm border border-solid border-polaris-border bg-polaris-muted"
                >
                  <Avatar
                    v-if="inbox.avatar_url"
                    :src="inbox.avatar_url"
                    :name="inbox.name"
                    :size="20"
                    rounded-full
                  />
                  <ChannelIcon
                    v-else
                    class="size-4 text-polaris-muted-foreground"
                    :inbox="inbox"
                  />
                </span>
                <span class="min-w-0">{{ inbox.name }}</span>
              </span>
            </BaseTableCell>
            <BaseTableCell>
              <ChannelName
                :channel-type="inbox.channel_type"
                :medium="inbox.medium"
                :voice-enabled="inbox.voice_enabled"
              />
            </BaseTableCell>
            <BaseTableCell actions>
              <RowMenu
                v-if="isAdmin"
                :label="t('SETTINGS_TABLE.ROW_ACTIONS', { name: inbox.name })"
                :items="rowMenuItems"
                @action="action => onRowAction(action, inbox)"
              />
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
